import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

function getImageUrl(type, partNo, colorId) {
  if (type === 'MINIFIG' || type === 'M') {
    return `https://img.bricklink.com/ItemImage/MN/0/${partNo}.png`;
  }
  return `https://img.bricklink.com/ItemImage/PN/${colorId}/${partNo}.png`;
}

function Stocking() {
  // ─── Walkthrough state ───
  const [stockingParts, setStockingParts] = useState(null);
  const [stockingSource, setStockingSource] = useState('');
  const [allParts, setAllParts] = useState([]); // flat list of all parts with bin info
  const [currentIdx, setCurrentIdx] = useState(0);
  const [walkthroughDone, setWalkthroughDone] = useState(false);

  // ─── Manual search state ───
  const [mode, setMode] = useState('loading'); // 'loading' | 'walkthrough' | 'manual'
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [targetBin, setTargetBin] = useState('');
  const [qty, setQty] = useState('');
  const [searching, setSearching] = useState(false);
  const [log, setLog] = useState([]);

  // ─── Load stocking parts on mount ───
  useEffect(() => {
    try {
      const raw = localStorage.getItem('stockingParts');
      const src = localStorage.getItem('stockingSource');
      if (raw) {
        const parts = JSON.parse(raw);
        if (parts.length > 0) {
          setStockingParts(parts);
          setStockingSource(src || 'Upload');

          // Sort by bin, then build flat list
          const sorted = [...parts].sort((a, b) => {
            const binA = (a.bin || '-INCOMING').toUpperCase();
            const binB = (b.bin || '-INCOMING').toUpperCase();
            return binA.localeCompare(binB);
          });

          setAllParts(sorted);
          setMode('walkthrough');
          return;
        }
      }
    } catch (e) {
      console.error('Error loading stocking parts:', e);
    }
    setMode('manual');
  }, []);

  // ─── Advance to next part ───
  const advancePart = useCallback(() => {
    if (currentIdx < allParts.length - 1) {
      setCurrentIdx(prev => prev + 1);
    } else {
      localStorage.removeItem('stockingParts');
      localStorage.removeItem('stockingSource');
      setWalkthroughDone(true);
    }
  }, [currentIdx, allParts.length]);

  function goBack() {
    if (currentIdx > 0) {
      setCurrentIdx(prev => prev - 1);
    }
  }

  // ─── Keyboard handler ───
  useEffect(() => {
    if (mode !== 'walkthrough' || walkthroughDone) return;

    function handleKey(e) {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        advancePart();
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault();
        goBack();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mode, walkthroughDone, advancePart]);

  function finishWalkthrough() {
    localStorage.removeItem('stockingParts');
    localStorage.removeItem('stockingSource');
    setWalkthroughDone(true);
  }

  // ─── Compute bin groups for the tab bar ───
  function getBinGroups() {
    const groups = {};
    allParts.forEach((p, i) => {
      const bin = (p.bin || '-INCOMING').toUpperCase();
      if (!groups[bin]) groups[bin] = { bin, startIdx: i, count: 0 };
      groups[bin].count++;
    });
    return Object.values(groups);
  }

  function getCurrentBin() {
    if (!allParts[currentIdx]) return '';
    return (allParts[currentIdx].bin || '-INCOMING').toUpperCase();
  }

  function getPartsInCurrentBin() {
    const bin = getCurrentBin();
    return allParts.filter(p => (p.bin || '-INCOMING').toUpperCase() === bin).length;
  }

  function getCurrentBinProgress() {
    const bin = getCurrentBin();
    let first = -1;
    let count = 0;
    allParts.forEach((p, i) => {
      if ((p.bin || '-INCOMING').toUpperCase() === bin) {
        if (first === -1) first = i;
        count++;
      }
    });
    if (first === -1) return '0 / 0';
    const placed = currentIdx - first + 1;
    return `${Math.min(placed, count)} / ${count}`;
  }

  // ─── Manual search ───
  async function handleSearch() {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${PROXY}/api/inventories`);
      const json = await res.json();
      const items = json.data || [];
      const q = search.toLowerCase();
      const matches = items.filter(lot =>
        (lot.item?.no || '').toLowerCase().includes(q) ||
        (lot.item?.name || '').toLowerCase().includes(q)
      );
      setResults(matches);
      setSelected(null);
    } catch (err) {
      console.error('Search error:', err);
    }
    setSearching(false);
  }

  function handleSelect(lot) {
    setSelected(lot);
    setTargetBin(lot.remarks || '');
    setQty(String(lot.quantity || ''));
  }

  async function handleStock() {
    if (!selected || !targetBin.trim()) return;
    const fromBin = selected.remarks || '(none)';
    const toBin = targetBin.trim().toUpperCase();
    try {
      await supabase.from('storage_history').insert({
        inventory_id: selected.inventory_id,
        part_no: selected.item?.no || '',
        color_name: selected.color_name || '',
        from_bin: fromBin,
        to_bin: toBin,
        moved_at: new Date().toISOString(),
      });
      setLog(prev => [{
        part: selected.item?.no || '',
        name: selected.item?.name || '',
        color: selected.color_name || '',
        from: fromBin,
        to: toBin,
        qty: qty,
        time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      }, ...prev]);
      setSelected(null);
      setSearch('');
      setResults([]);
      setTargetBin('');
      setQty('');
    } catch (err) {
      console.error('Stock error:', err);
    }
  }

  // ─── Render: Loading ───
  if (mode === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Stocking Mode" icon="↓" />
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>Loading...</div>
      </div>
    );
  }

  // ─── Render: Walkthrough complete ───
  if (walkthroughDone) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Stocking Mode" icon="↓" />
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: colors.greenDark }}>✓</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: colors.greenDark, marginBottom: '8px' }}>
            All Stocked!
          </div>
          <div style={{ fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '24px' }}>
            {allParts.length} parts placed across {getBinGroups().length} bins
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px', background: colors.navy, color: '#fff',
                border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
              }}
            >Back to Dashboard</button>
            <button
              onClick={() => { setMode('manual'); setWalkthroughDone(false); }}
              style={{
                padding: '12px 24px', background: colors.cardBg, color: colors.navy,
                border: `1px solid ${colors.border}`, borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >Manual Search Mode</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Walkthrough mode (one part at a time) ───
  if (mode === 'walkthrough' && allParts.length > 0) {
    const current = allParts[currentIdx];
    const currentBin = getCurrentBin();
    const binGroups = getBinGroups();

    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Stocking Mode" icon="↓" />

        {/* Source banner */}
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
          padding: '10px 16px', marginBottom: '16px', fontSize: '0.78rem', color: '#1e40af',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>Source: {stockingSource}</span>
          <span>{currentIdx + 1} / {allParts.length}</span>
        </div>

        {/* Overall progress bar */}
        <div style={{
          width: '100%', height: '6px', background: '#e2e4e8',
          borderRadius: '3px', overflow: 'hidden', marginBottom: '16px',
        }}>
          <div style={{
            width: `${((currentIdx + 1) / allParts.length) * 100}%`,
            height: '100%', background: colors.greenDark, borderRadius: '3px',
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Bin tabs */}
        <div style={{
          display: 'flex', gap: '6px', marginBottom: '16px',
          overflowX: 'auto', paddingBottom: '4px',
        }}>
          {binGroups.map((g) => {
            const isActive = g.bin === currentBin;
            const isDone = g.startIdx + g.count <= currentIdx;
            return (
              <button
                key={g.bin}
                onClick={() => setCurrentIdx(g.startIdx)}
                style={{
                  padding: '8px 14px',
                  background: isActive ? colors.navy : isDone ? '#f0fdf4' : colors.cardBg,
                  color: isActive ? '#fff' : isDone ? colors.greenDark : colors.textPrimary,
                  border: `1px solid ${isActive ? colors.navy : isDone ? '#bbf7d0' : colors.border}`,
                  borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                  fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                {isDone ? '✓ ' : ''}{g.bin} ({g.count})
              </button>
            );
          })}
        </div>

        {/* Current bin header */}
        <div style={{
          background: colors.navy, borderRadius: '6px', padding: '16px 20px',
          marginBottom: '20px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ffffff' }}>{currentBin}</div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
              {getCurrentBinProgress()} in this bin
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
            Part {currentIdx + 1} of {allParts.length}
          </div>
        </div>

        {/* Current part — big card */}
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', padding: '30px 20px', textAlign: 'center',
          marginBottom: '20px',
        }}>
          <img
            src={getImageUrl(current.type, current.partNo, current.colorId)}
            alt={current.partNo}
            style={{
              width: '160px', height: '160px', objectFit: 'contain',
              background: '#f9fafb', borderRadius: '8px', padding: '10px',
              marginBottom: '16px',
            }}
            onError={e => { e.target.style.opacity = '0.15'; }}
          />
          <div style={{
            fontSize: '1.3rem', fontWeight: 800, color: colors.navy, marginBottom: '6px',
          }}>{current.partNo}</div>
          <div style={{
            fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '4px',
          }}>{current.color}</div>
          <div style={{
            fontSize: '2rem', fontWeight: 800, color: colors.navy, marginTop: '12px',
          }}>x{current.qty}</div>
        </div>

        {/* Placed button */}
        <button
          onClick={advancePart}
          style={{
            width: '100%', padding: '20px', background: colors.greenDark,
            color: '#fff', border: 'none', borderRadius: '6px',
            fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '1.2rem',
            cursor: 'pointer', marginBottom: '10px',
            transition: 'background 0.15s ease',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#4ca832'}
          onMouseOut={e => e.currentTarget.style.background = colors.greenDark}
        >
          ✓ Placed
        </button>

        {/* Spacebar hint */}
        <div style={{
          textAlign: 'center', fontSize: '0.72rem', color: colors.textSecondary,
          marginBottom: '16px',
        }}>
          or press <span style={{
            background: '#e2e4e8', padding: '2px 8px', borderRadius: '4px',
            fontWeight: 700, fontSize: '0.7rem',
          }}>SPACE</span> to advance
        </div>

        {/* Back / Skip row */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={goBack}
            disabled={currentIdx === 0}
            style={{
              flex: 1, padding: '14px', background: colors.cardBg,
              color: currentIdx === 0 ? colors.border : colors.navy,
              border: `1px solid ${colors.border}`, borderRadius: '6px',
              fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.9rem',
              cursor: currentIdx === 0 ? 'not-allowed' : 'pointer',
            }}
          >← Back</button>
          <button
            onClick={finishWalkthrough}
            style={{
              flex: 1, padding: '14px', background: colors.cardBg,
              color: colors.textSecondary, border: `1px solid ${colors.border}`,
              borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
              fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
            }}
          >End Early</button>
        </div>
      </div>
    );
  }

  // ─── Render: Manual search mode ───
  return (
    <div style={{
      minHeight: '100vh', background: colors.bg, padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Stocking Mode" icon="↓" />

      {/* Search */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search part # or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          style={{
            flex: 1, padding: '14px 16px', background: colors.cardBg,
            border: `1px solid ${colors.border}`, borderRadius: '6px',
            color: colors.textPrimary, fontFamily: "'Outfit', sans-serif",
            fontSize: '0.95rem', outline: 'none',
          }}
        />
        <button
          onClick={handleSearch}
          style={{
            padding: '14px 24px', background: colors.navy, color: '#ffffff',
            border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 800,
            fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
          }}
        >Search</button>
      </div>

      {searching && (
        <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>Searching...</div>
      )}

      {/* Selected part — stocking form */}
      {selected && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderLeft: `4px solid ${colors.greenDark}`, borderRadius: '6px',
          padding: '20px', marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
            <img
              src={`https://img.bricklink.com/ItemImage/PN/${selected.color_id || 0}/${selected.item?.no || ''}.png`}
              alt={selected.item?.name || ''}
              style={{
                width: '60px', height: '60px', objectFit: 'contain',
                background: '#f9fafb', borderRadius: '6px', padding: '4px',
              }}
              onError={(e) => { e.target.style.opacity = '0.15'; }}
            />
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: colors.textPrimary }}>{selected.item?.name || 'Unknown'}</div>
              <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>{selected.item?.no} · {selected.color_name} · Current bin: {selected.remarks || '(none)'}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '0.72rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Stock to Bin</div>
              <input
                type="text"
                value={targetBin}
                onChange={(e) => setTargetBin(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  color: colors.textPrimary, fontFamily: "'Outfit', sans-serif",
                  fontSize: '1rem', fontWeight: 700, outline: 'none',
                  textAlign: 'center', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Quantity</div>
              <input
                type="number"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  color: colors.textPrimary, fontFamily: "'Outfit', sans-serif",
                  fontSize: '1rem', fontWeight: 700, outline: 'none',
                  textAlign: 'center', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
          <button
            onClick={handleStock}
            style={{
              width: '100%', padding: '14px', background: colors.navy,
              color: '#ffffff', border: 'none', borderRadius: '6px',
              fontSize: '1rem', fontWeight: 800, fontFamily: "'Outfit', sans-serif",
              cursor: 'pointer',
            }}
          >Stock Part</button>
        </div>
      )}

      {/* Search results */}
      {!selected && results.length > 0 && (
        <>
          <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginBottom: '10px' }}>
            {results.length} matches — tap to select
          </div>
          {results.slice(0, 20).map((lot, i) => (
            <div
              key={i}
              onClick={() => handleSelect(lot)}
              style={{
                background: colors.cardBg, border: `1px solid ${colors.border}`,
                borderRadius: '6px', padding: '12px 14px', marginBottom: '8px',
                display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer',
              }}
            >
              <img
                src={`https://img.bricklink.com/ItemImage/PN/${lot.color_id || 0}/${lot.item?.no || ''}.png`}
                alt={lot.item?.name || ''}
                style={{
                  width: '40px', height: '40px', objectFit: 'contain',
                  background: '#f9fafb', borderRadius: '6px', padding: '3px',
                }}
                onError={(e) => { e.target.style.opacity = '0.15'; }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>{lot.item?.name || 'Unknown'}</div>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>{lot.item?.no} · {lot.color_name}</div>
              </div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.navy }}>{lot.remarks || '—'}</div>
            </div>
          ))}
        </>
      )}

      {/* Empty state */}
      {!selected && results.length === 0 && !searching && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', padding: '40px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>↓</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>Manual Stocking</div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary, lineHeight: 1.6 }}>
            Search for a part to log where you stocked it.
            <br />To use the guided walkthrough, start from Upload or Part Out.
          </div>
        </div>
      )}

      {/* Session log */}
      {log.length > 0 && (
        <>
          <div style={{
            fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary,
            marginTop: '28px', marginBottom: '10px',
          }}>Session Log ({log.length} stocked)</div>
          {log.map((entry, i) => (
            <div key={i} style={{
              background: colors.cardBg, border: `1px solid ${colors.border}`,
              borderRadius: '6px', padding: '10px 14px', marginBottom: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>{entry.part}</div>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>{entry.color} · x{entry.qty}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem' }}>
                  <span style={{ color: '#dc2626' }}>{entry.from}</span>
                  <span style={{ color: colors.textSecondary }}>→</span>
                  <span style={{ color: colors.greenDark, fontWeight: 700 }}>{entry.to}</span>
                </div>
                <div style={{ fontSize: '0.65rem', color: colors.textSecondary, marginTop: '2px' }}>{entry.time}</div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default Stocking;